import React, { memo } from "react";

import HeaderWatchList from "@/components/organisms/HeaderWatchList";
import PlayerDetail from "@/components/organisms/PlayerDetail";
import PlayerList from "@/components/organisms/PlayerList";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import {
  RecommendationsPlayerFragment,
  WatchlistWatchlistMetaFragment,
} from "@/generated/graphql";
import { SwipeableProvider } from "@/providers/Swipeable";
import { TypeRightColumn } from "@/stores/player";
import { PlayerCardData } from "@/types/playerCardTypes";

/**
 * Props interface for MainView component.
 */
interface IMyRoster {
  /** Whether initial player data is loading */
  loading: boolean;
  /** Whether pull-to-refresh is active */
  refreshing: boolean;
  /** Array of player data to display */
  players: PlayerCardData[];
  /** Array of watchlists for filtering */
  watchlists: WatchlistWatchlistMetaFragment[];
  /** Height for recommendations section */
  heightRecommendation?: number;
  /** Callback when scrolling near end to load more players */
  onLoadMore: () => void;
  /** Callback to refresh player data */
  onRefresh: () => void;
  /** Player display type identifier */
  type: TypeRightColumn;
  /** Currently selected player ID */
  playerId?: string;
  /** Detailed data for selected player */
  playerDetail?: PlayerCardData | RecommendationsPlayerFragment;
  /** Whether to use mobile layout (vs tablet/desktop) */
  isSmallScreen: boolean;
  /** Whether the viewport is at least tablet width */
  isTabletWidth: boolean;
  /** Whether watchlist tab is active */
  isOnWatchlistTab?: boolean;
}

/**
 * Main view for roster management displaying player list and details.
 * @param root0 - Component props
 * @param root0.loading - Whether initial player data is loading
 * @param root0.refreshing - Whether pull-to-refresh is active
 * @param root0.players - Array of player data to display
 * @param root0.watchlists - Array of watchlists for filtering
 * @param root0.heightRecommendation - Height for recommendations section
 * @param root0.isSmallScreen - Whether to use mobile layout (vs tablet/desktop)
 * @param root0.isTabletWidth - Whether the viewport is at least tablet width
 * @param root0.type - Player display type identifier
 * @param root0.playerId - Currently selected player ID
 * @param root0.playerDetail - Detailed data for selected player
 * @param root0.onLoadMore - Callback when scrolling near end to load more players
 * @param root0.onRefresh - Callback to refresh player data
 */
const MainView = ({
  loading,
  refreshing,
  players,
  watchlists,
  heightRecommendation,
  isSmallScreen,
  isTabletWidth,
  type,
  playerId,
  playerDetail,
  onLoadMore,
  onRefresh,
}: IMyRoster) =>
  isSmallScreen ? (
    <SwipeableProvider>
      <Box className="flex-1 p-3">
        <HeaderWatchList
          watchlists={watchlists}
          isShowPopoverFilter={loading || !!players.length}
          isShowHeaderSearch={isTabletWidth}
          watchListVisible
        />
        <PlayerList
          loading={loading}
          refreshing={refreshing}
          heightRecommendation={heightRecommendation}
          onLoadMore={onLoadMore}
          onRefresh={onRefresh}
          players={players}
        />
      </Box>
    </SwipeableProvider>
  ) : (
    <Box className="flex-1">
      <Stack direction="horizontal" className="flex-1">
        <PlayerDetail
          loading={loading}
          loadWatchList={loading}
          onCallbackOpen={undefined}
          heightRecommendation={heightRecommendation}
          playerId={playerId}
          type={type}
          isRefreshing={refreshing}
          players={players}
          activeWatchlist={null}
          watchlists={watchlists}
          onLoadMore={onLoadMore}
          onRefresh={onRefresh}
          playerDetail={playerDetail}
          onDragEnd={undefined}
        />
      </Stack>
    </Box>
  );

MainView.displayName = "MainView";

export default memo(MainView);
